import { useTranslation, useUserSettings } from "@next-platform/client-common-core";
import { SubCapabilityKey, useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import { useEventCapabilityIsUsed } from "@next-platform/client-common-core/services/databricks";
import { Button, ButtonVariant, IconVariants, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { memo, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";

import PageMetaTags from "@/components/common/PageMetaTags";
import Trans from "@/components/Trans/Trans";
import { ROUTES } from "@/constants";
import useGuestSignIn from "@/hooks/useGuestSignIn";
import useIsLoggedOutReader from "@/hooks/useIsLoggedOutReader";
import useLocalizedNavigation from "@/hooks/useLocalizedNavigation";
import MediaFileDetails from "@/pages/Gallery/components/MediaFileDetails/MediaFileDetails";
import getShouldBlurNsfwAvatars from "@/utils/getShouldBlurNsfwAvatars";

type MediaFileProps = {
  readonly "data-testid"?: string;
};

const MediaFile = ({ "data-testid": testId = "MediaFile" }: MediaFileProps) => {
  const { id: routeId } = useParams<{ id: string }>();
  // A logged out reader gets no file and no back control, only the sign in overlay.
  const isLoggedOutReader = useIsLoggedOutReader();
  useGuestSignIn({ isEnabled: isLoggedOutReader });
  const id = isLoggedOutReader ? undefined : routeId;
  const { t } = useTranslation();
  const navigate = useLocalizedNavigation();
  const { blurNSFW, showNSFW } = useUserSettings();
  const isNsfwUserToggleAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.NsfwUserToggle);
  // Two settings hide adult content: "show NSFW" off means never show it uncovered, and "blur NSFW" is the
  // softer preference for a reader who does see it. Turning the first off clears the second, so both are read.
  const shouldBlurNsfw = !showNSFW || getShouldBlurNsfwAvatars({ isNsfwUserToggleAllowed, blurNSFW });
  const raiseEventCapabilityIsUsed = useEventCapabilityIsUsed();
  // One usage per file reached at its own address, whether by a link, a reload or a shared link opened cold.
  useEffect(() => {
    if (id) {
      raiseEventCapabilityIsUsed(SubCapabilityKey.MediaMediaPage);
    }
  }, [id, raiseEventCapabilityIsUsed]);

  const handleBackClick = useCallback(() => {
    navigate(ROUTES.myGallery);
  }, [navigate]);

  return (
    <div data-testid={testId} className="w-full bg-background text-gray-12 dark:text-white">
      <PageMetaTags title={t("mediaFile:page.title")} data-testid="MediaFile-PageMetaTags" />
      <h1 className="sr-only" data-testid="MediaFile-Heading">
        <Trans i18nKey="mediaFile:page.title" />
      </h1>
      {isLoggedOutReader ? null : (
        <div className="mx-auto flex w-full max-w-[932px] flex-col gap-lg px-4 py-4">
          <Button
            variant={ButtonVariant.TRANSPARENT}
            iconStart={IconVariants.ARROW_LEFT}
            onClick={handleBackClick}
            className="h-6 !w-auto self-start !justify-start !p-0 text-gray-11"
            data-testid="MediaFile-BackButton"
          >
            <Text as="span" variant={TextVariants.LABEL_LARGE_MEDIUM}>
              <Trans i18nKey="mediaFile:page.backToGallery" />
            </Text>
          </Button>
          <div className="w-full lg:h-[min(700px,calc(100dvh-180px))]">
            {id ? <MediaFileDetails id={id} shouldBlurNsfw={shouldBlurNsfw} /> : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(MediaFile);
